import { Datagrid, TextField, Title, useGetList, Button as RaButton, useNotify, DateField, FunctionField } from 'react-admin';
import { Box, Button, Typography, Stack, Skeleton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useState, useEffect } from 'react';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';

interface Complaint {
    id: string;
    lastUpdatedDate: string;
    complaintType: string;
    complaintStatus: 'NEW' | 'IN_PROGRESS' | 'REJECTED' | 'APPLIED';
    meta?: {
        cursor?: string | null;
    };
}

const complaintStatusOptions = [
    { value: 'ALL', label: 'Все' },
    { value: 'NEW', label: 'Открытые' },
    { value: 'IN_PROGRESS', label: 'Рассматриваются' },
    { value: 'REJECTED', label: 'Отклоненные' },
    { value: 'APPLIED', label: 'Приняты меры' }
];

const complaintTypeLabels: Record<string, string> = {
    'INCORRECT_PRICE': 'Некорректная цена',
    'INCORRECT_DESCRIPTION_OR_PHOTOS': 'Некорректное описание или фото',
    'NOT_ACTUAL_AD': 'Неактуальное объявление',
    'SCAM': 'Скам',
    'FAKE': 'Подделка вещи'
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'NEW':
            return 'error';
        case 'IN_PROGRESS':
            return 'warning';
        case 'REJECTED':
            return 'default';
        case 'APPLIED':
            return 'success';
        default:
            return 'default';
    }
};

const getStatusLabel = (status: string) => {
    switch (status) {
        case 'NEW':
            return 'Открытая';
        case 'IN_PROGRESS':
            return 'Рассматривается';
        case 'REJECTED':
            return 'Отклонена';
        case 'APPLIED':
            return 'Приняты меры';
        default:
            return status;
    }
};

export const ComplaintsList = () => {
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [allComplaints, setAllComplaints] = useState<Complaint[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('NEW');
    const [showBanner, setShowBanner] = useState(true);
    const notify = useNotify();

    const {
        data,
        isLoading,
        refetch
    } = useGetList<Complaint>(
        'complaints',
        {
            pagination: { page: 1, perPage: 30 },
            sort: { field: 'lastUpdatedDate', order: 'DESC' },
            meta: { cursor },
            filter: { complaintStatusFilter: statusFilter === 'ALL' ? undefined : statusFilter }
        }
    );

    useEffect(() => {
        if (data && data.length > 0) {
            const lastComplaint = data[data.length - 1];
            const newCursor = lastComplaint.meta?.cursor;
            setHasMore(newCursor !== null && newCursor !== undefined);
        } else {
            setHasMore(false);
        }
    }, [data]);

    const handleLoadMore = async () => {
        if (data && data.length > 0) {
            const lastComplaint = data[data.length - 1];
            const newCursor = lastComplaint.meta?.cursor;
            if (newCursor) {
                setCursor(newCursor);
                setAllComplaints(prev => [...prev, ...data]);
                await refetch();
            } else {
                setHasMore(false);
            }
        }
    };

    const handleStatusFilterChange = (newStatus: string) => {
        setStatusFilter(newStatus);
        setCursor(undefined);
        setAllComplaints([]);
        setHasMore(true);
    };

    const handleCopyId = (id: string) => {
        navigator.clipboard.writeText(id);
        notify('ID скопирован в буфер обмена', { type: 'success' });
    };

    const currentComplaints = data || [];
    const displayComplaints = [...allComplaints, ...currentComplaints];

    const LoadingSkeleton = () => (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1}>
                {[...Array(5)].map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Skeleton variant="text" width={200} />
                        <Skeleton variant="text" width={150} />
                        <Skeleton variant="text" width={180} />
                        <Skeleton variant="text" width={120} />
                        <Skeleton variant="text" width={100} />
                    </Box>
                ))}
            </Stack>
        </Box>
    );

    if (isLoading && allComplaints.length === 0) {
        return (
            <Box>
                <Title title="Жалобы пользователей" />
                <Box sx={{ mt: 2 }}>
                    <LoadingSkeleton />
                </Box>
            </Box>
        );
    }

    if (displayComplaints.length === 0 && !isLoading) {
        return (
            <Box>
                <Title title="Жалобы пользователей" />
                {showBanner && (
                    <Alert
                        severity="info"
                        sx={{ mt: 2, mb: 3, alignItems: 'center', position: 'relative', pr: 6 }}
                        icon={false}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setShowBanner(false)}
                                sx={{ position: 'absolute', right: 8, top: 8 }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Статусы жалоб:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li><b style={{ color: '#f44336' }}>Открытые</b> — новые жалобы, требующие рассмотрения.</li>
                            <li><b style={{ color: '#ff9800' }}>Рассматриваются</b> — жалобы в процессе модерации.</li>
                            <li><b style={{ color: '#9e9e9e' }}>Отклонены</b> — жалобы, которые были отклонены модератором.</li>
                            <li><b style={{ color: '#4caf50' }}>Приняты меры</b> — жалобы, по которым были приняты меры.</li>
                        </ul>
                    </Alert>
                )}
                
                <Box sx={{ mb: 3 }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Фильтр по статусу</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Фильтр по статусу"
                            onChange={(e) => handleStatusFilterChange(e.target.value)}
                        >
                            {complaintStatusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                        Список жалоб пуст
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Title title="Жалобы пользователей" />
            {showBanner && (
                <Alert
                    severity="info"
                    sx={{ mt: 2, mb: 3, alignItems: 'center', position: 'relative', pr: 6 }}
                    icon={false}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setShowBanner(false)}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Статусы жалоб:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li><b style={{ color: '#f44336' }}>Открытые</b> — новые жалобы, требующие рассмотрения.</li>
                        <li><b style={{ color: '#ff9800' }}>Рассматриваются</b> — жалобы в процессе модерации.</li>
                        <li><b style={{ color: '#9e9e9e' }}>Отклонены</b> — жалобы, которые были отклонены модератором.</li>
                        <li><b style={{ color: '#4caf50' }}>Приняты меры</b> — жалобы, по которым были приняты меры.</li>
                    </ul>
                </Alert>
            )}
            
            <Box sx={{ mb: 3 }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Фильтр по статусу</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Фильтр по статусу"
                        onChange={(e) => handleStatusFilterChange(e.target.value)}
                    >
                        {complaintStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Datagrid 
                    data={displayComplaints}
                    rowClick="show"
                    bulkActionButtons={false}
                    sx={{ '& .MuiTableCell-root': { py: 2 } }}
                >
                    <TextField 
                        source="id" 
                        label="ID" 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopyId(e.currentTarget.textContent || '');
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    />
                    <DateField source="lastUpdatedDate" label="Дата обновления" showTime />
                    <FunctionField
                        label="Тип жалобы"
                        render={record => {
                            const type = record?.complaintType;
                            return complaintTypeLabels[type] || type;
                        }}
                    />
                    <FunctionField
                        label="Статус"
                        render={record => {
                            const status = record?.complaintStatus;
                            if (!status) return null;
                            return (
                                <Chip 
                                    label={getStatusLabel(status)} 
                                    color={getStatusColor(status) as any}
                                    size="small"
                                />
                            );
                        }}
                    />
                </Datagrid>
            </Box>
            {hasMore && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={handleLoadMore}>
                        Загрузить еще
                    </Button>
                </Stack>
            )}
        </Box>
    );
};
